import type { RouterOutputs } from "@apps/server/trpc/routers";
import type { BadgeProps, MantineColor } from "@mantine/core";
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Drawer,
  Group,
  Kbd,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { getServiceCategory } from "@packages/lib";
import {
  IconBriefcase,
  IconCreditCard,
  IconDeviceFloppy,
  IconExternalLink,
  IconFileSearch,
  IconMapPin,
  IconRestore,
  IconUser,
} from "@tabler/icons-react";
import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { Activity, useMemo } from "react";
import { HotkeyTooltip } from "~/components/hotkey";
import { AddressField } from "~/components/input-fields/address-field";
import { CrmField } from "~/components/input-fields/crm-field";
import { Section } from "~/components/view/section";
import { useOptionLabel } from "~/dexie/hooks";
import { useConfigStore } from "~/dexie/store";
import { useBoolean } from "~/hooks/use-boolean";
import { useCrmTableView } from "~/hooks/use-crm-table-view";
import { CustomerView } from "../customers/$customerId";
import { NotesDrawer } from "./-components/notes-drawer";
import { OneflowButton } from "./-components/oneflow";

type Deal = NonNullable<RouterOutputs["deal"]["getDeal"]>;
type Customer = NonNullable<Deal["customer"]>;

export const Route = createFileRoute("/_authenticated/deals/$dealId")({
  component: DealComponent,
  loader: async ({ context: { queryClient, trpc }, params }) => {
    const dealId = Number(params.dealId);
    const deal = await queryClient.ensureQueryData(
      trpc.deal.getDeal.queryOptions({
        id: dealId,
      }),
    );
    if (!deal) throw notFound();
    const serviceName = useConfigStore.getState().getOptionLabel("crm.deal.service", deal.service);
    return { deal, serviceName };
  },
  head: ({ loaderData }) => {
    const title = `Affär - ${[loaderData?.serviceName, loaderData?.deal.customer?.name].filter(Boolean).join(" - ")}`;
    return {
      meta: [{ title }],
    };
  },
  notFoundComponent: DealNotFound,
});

function DealNotFound() {
  const navigate = useNavigate();
  return (
    <Center h="60vh">
      <Stack align="center" gap="md">
        <IconFileSearch size={64} stroke={1.5} color="var(--mantine-color-dimmed)" />
        <Title order={3}>Deal not found</Title>
        <Text c="dimmed" size="sm" ta="center">
          The deal you're looking for doesn't exist or may have been deleted.
        </Text>
        <Button variant="light" onClick={() => navigate({ to: "/deals" })}>
          Back to Deals
        </Button>
      </Stack>
    </Center>
  );
}

function DealComponent() {
  const loaderData = Route.useLoaderData();
  const initialAddressLabel = useConfigStore((state) => state.getConfigByField("crm.deal.initialAddressId")?.label);
  const finalAddressLabel = useConfigStore((state) => state.getConfigByField("crm.deal.finalAddressId")?.label);
  const isSingleColumn = useMediaQuery("(max-width: 1440px)");

  const view = useCrmTableView("crm.deal", {
    id: loaderData.deal.id,
    initialData: loaderData.deal,
  });

  const serviceCategory = useMemo(() => {
    return getServiceCategory(view.values?.service);
  }, [view.values]);

  return (
    <Box>
      <Group px="lg" py="md">
        <Title order={2}>
          {loaderData.deal.customer?.name ?? "Unknown"} {loaderData.serviceName ? ` - ${loaderData.serviceName}` : ""}
        </Title>
        <DealStatusBadge status={view.values?.status} />
      </Group>

      <Divider />

      <Group justify="flex-end" my="lg" px="lg">
        <TempInfo info={loaderData.deal.tempData} />
        <NotesDrawer dealId={loaderData.deal.id} />
        <OneflowButton deal={loaderData.deal} />
        <Button.Group>
          <Tooltip p={0} label={<Kbd size="lg">{formatForDisplay("Mod+R")}</Kbd>}>
            <Button
              size="xs"
              variant="light"
              disabled={!view.isDirty || view.isUpdatePending}
              leftSection={<IconRestore size={16} />}
              onClick={view.reset}>
              Reset
            </Button>
          </Tooltip>
          <Tooltip p={0} label={<Kbd size="lg">{formatForDisplay("Mod+S")}</Kbd>}>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={view.isUpdatePending}
              disabled={!view.isDirty}
              onClick={view.saveChanges}>
              Save
            </Button>
          </Tooltip>
        </Button.Group>
      </Group>
      <Container pb="lg" fluid={!isSingleColumn}>
        <Box style={{ columns: isSingleColumn ? 1 : 2, columnGap: "var(--mantine-spacing-lg)" }}>
          {loaderData.deal.customer && <CustomerSection customer={loaderData.deal.customer} />}

          <Section title="Affär" mb="lg" icon={IconBriefcase}>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("serviceDate")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("status")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("service")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("source")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("price")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("kmCost")} />
            </Section.Col>
            <Section.Col width="100%">
              <CrmField {...view.getFieldProps("description")} as="textarea" />
            </Section.Col>
            <Section.Col width="100%">
              <CrmField {...view.getFieldProps("infoFromCustomer")} as="textarea" />
            </Section.Col>

            <Section.Col width="25%">
              <CrmField {...view.getFieldProps("rut")} />
            </Section.Col>
            <Section.Col width="25%">
              <CrmField {...view.getFieldProps("moms")} />
            </Section.Col>
          </Section>

          <Section title={serviceCategory === "moving" ? "From Address" : "Address"} mb="lg" icon={IconMapPin}>
            <Section.Col width="100%">
              <AddressField
                label={initialAddressLabel}
                addressId={view.values?.initialAddressId}
                createWhenEmpty={{
                  action: true,
                  addRelation: { table: "deal", field: "initialAddressId", id: loaderData.deal.id },
                }}
              />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("floor1")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("area1")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("entryCode1")} />
            </Section.Col>
            <Section.Col width="50%">
              <CrmField {...view.getFieldProps("elevator1")} />
            </Section.Col>
          </Section>

          <Activity mode={serviceCategory === "moving" ? "visible" : "hidden"}>
            <Section title="To Address" mb="lg" icon={IconMapPin}>
              <Section.Col width="100%">
                <AddressField
                  label={finalAddressLabel}
                  addressId={view.values?.finalAddressId}
                  createWhenEmpty={{
                    action: true,
                    addRelation: { table: "deal", field: "finalAddressId", id: loaderData.deal.id },
                  }}
                />
              </Section.Col>
              <Section.Col width="33%">
                <CrmField {...view.getFieldProps("floor2")} />
              </Section.Col>
              <Section.Col width="33%">
                <CrmField {...view.getFieldProps("area2")} />
              </Section.Col>
              <Section.Col width="33%">
                <CrmField {...view.getFieldProps("entryCode2")} />
              </Section.Col>
              <Section.Col width="50%">
                <CrmField {...view.getFieldProps("elevator2")} />
              </Section.Col>
            </Section>
          </Activity>

          <Section title="Payment" mb="lg" icon={IconCreditCard}>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("totalAmount")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("totalWithoutVat")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("balance")} />
            </Section.Col>

            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("totalToPay")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("paidAmount")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("leftToPay")} />
            </Section.Col>

            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("rutAmount")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("rutReceived")} />
            </Section.Col>
            <Section.Col width="33%" pt={40}>
              <CrmField {...view.getFieldProps("rutApplied")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("paymentMethod")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("paymentTerms")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("dueDate")} />
            </Section.Col>
            <Section.Col width="33%">
              <CrmField {...view.getFieldProps("mainOcr")} />
            </Section.Col>
          </Section>
        </Box>
      </Container>
    </Box>
  );
}

function CustomerSection(props: { customer: Customer }) {
  const drawer = useBoolean();

  const view = useCrmTableView("crm.customer", {
    id: props.customer.id,
    fields: ["name", "email", "phone", "type", "identificationNumber", "businessRepresentative"],
    initialData: props.customer,
    readOnly: `You can click the "View" button to view and edit customer details.`,
  });

  useHotkey("Mod+K", drawer.toggle);

  return (
    <Section
      title="Kund"
      rightSection={
        <>
          <HotkeyTooltip hotkey="Mod+K">
            <Button size="xs" variant="light" leftSection={<IconExternalLink size={16} />} onClick={drawer.open}>
              View
            </Button>
          </HotkeyTooltip>
          <Drawer opened={drawer.state} onClose={drawer.close} size={800}>
            <CustomerView customerId={props.customer.id} initialData={props.customer} />
          </Drawer>
        </>
      }
      icon={IconUser}
      mb="lg">
      <Section.Col width="50%">
        <CrmField {...view.getFieldProps("name")} />
      </Section.Col>
      <Section.Col width="50%">
        <CrmField {...view.getFieldProps("email")} />
      </Section.Col>
      <Section.Col width="50%">
        <CrmField {...view.getFieldProps("phone")} />
      </Section.Col>
      <Section.Col width="50%">
        <CrmField {...view.getFieldProps("identificationNumber")} />
      </Section.Col>
      <Section.Col width="50%">
        <CrmField {...view.getFieldProps("type")} />
      </Section.Col>
      {props.customer.type === "business" && (
        <Section.Col width="50%">
          <CrmField {...view.getFieldProps("businessRepresentative")} />
        </Section.Col>
      )}
    </Section>
  );
}

type DealStatusBadgeProps = {
  status: Deal["status"];
  statusLabel?: string;
} & Omit<BadgeProps, "color">;

export function DealStatusBadge({ status, statusLabel, ...props }: DealStatusBadgeProps) {
  const color = useMemo((): { bg: MantineColor; text: MantineColor } => {
    if (status === "agreement") return { bg: "green.8", text: "gray.0" };
    if (status === "partially_signed") return { bg: "violet", text: "gray.0" };
    if (status === "offer") return { bg: "cyan", text: "gray.0" };
    if (status === "rejected" || status === "rejected_no_answer" || status === "cancelled")
      return { bg: "red", text: "gray.0" };
    if (status === "expired" || status === "irrelevant_lead") return { bg: "gray", text: "gray.0" };
    if (status === "lead") return { bg: "yellow.8", text: "gray.0" };
    return { bg: "gray", text: "gray.0" };
  }, [status]);

  const label = useOptionLabel("crm.deal.status", status);

  return (
    <Badge miw={120} size="lg" color={color.bg} c={color.text} mt={8} {...props}>
      {label}
    </Badge>
  );
}

function TempInfo(props: { info: Record<string, any> }) {
  const drawer = useBoolean();
  const hasInfo = Object.keys(props.info).length > 0;

  useHotkey("Mod+I", drawer.toggle, { enabled: hasInfo });

  return (
    <>
      <Tooltip p={0} label={<Kbd size="lg">{formatForDisplay("Mod+I")}</Kbd>}>
        <Button size="xs" disabled={!hasInfo}>
          More Info
        </Button>
      </Tooltip>
      <Drawer opened={drawer.state} onClose={drawer.close} title={<Title order={3}>Additional info from sheets</Title>}>
        <Table>
          <Table.Tbody>
            {Object.entries(props.info).map(([key, value]) => (
              <Table.Tr key={key}>
                <Table.Td w={20}>
                  <Text c="dimmed">{key}</Text>
                </Table.Td>
                <Table.Td>
                  <Text>{String(value)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Drawer>
    </>
  );
}
